import { makeStyles } from 'tss-react/mui';
import React from 'react';
import palette from '@ui/palette';
import { Box } from '@mui/material';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { useCanSelectProject } from 'abilities/projects';
import { Project } from './types';
import {
  getProjectVersionIcon,
  getProjectVersionInfo,
} from '../../../util/projectVersioningUtils';
import useProjectAccessAbility from './hooks/useProjectAccessAbility';
import { useUserContext } from '../../../context/user/UserContext';
import { ProjectAccessProvider } from '../../../context/projectAccess/ProjectAccessContext';
import Card, { CardProps } from '../Card';
import CardHeader, { CardHeaderProps } from '../CardHeader';
import IconCheckboxInput, {
  IconCheckboxInputProps,
} from '../../Inputs/IconCheckboxInput';
import CardFooter from '../CardFooter';
import ProjectActionMenu from './ProjectActionMenu';
import CardDescription from '../CardDescription';
import {
  Drive,
  DriveAccessRoleEnum,
  Folder,
  StudioProjectVersion,
  TeamMember,
} from '../../../../server/graphql/__generated.types';
import { UserContextQuery } from '../../../context/user/operation.generated';
import { DriveType } from '../../Drives/types';
import OwnerBubble from './OwnerBubble';

export interface ProjectCardProps
  extends Pick<CardHeaderProps, 'href' | 'slots'>,
    Pick<CardProps, 'selected' | 'onClick' | 'onKeyPress'> {
  project: Project;
  onSelect?: (isSelected: boolean) => void;
  currentDrive?: Drive | null;
  currentFolder?: Folder;
  disableSelect?: boolean;
  pageOrigin?: ProjectCardOriginPage;
}

export enum ProjectCardOriginPage {
  PageResults = 'PageResults',
  WorkspacesLanding = 'WorkspacesLanding',
  StudioNavbar = 'StudioNavbar',
}

interface BaseProjectCardProps extends ProjectCardProps {
  teamData: UserContextQuery;
}

interface ProjectCardHeaderProps
  extends CardHeaderProps,
    Omit<IconCheckboxInputProps, 'type'>,
    Pick<ProjectCardProps, 'disableSelect'> {}

export const PROJECT_CARD_MIN_WIDTH = 232;
export const PROJECT_CARD_HEIGHT = 148;
export const WELLSAID_USER_ID = 'wellsaid';

const ProjectCardBody = ({
  clientName,
  studioVersion,
}: Pick<Project, 'clientName' | 'studioVersion'>) => {
  const showCarusoLegacy = studioVersion === StudioProjectVersion.V2;
  const versionDisplayName = getProjectVersionInfo(
    studioVersion,
    showCarusoLegacy
  ).displayName;
  const icon =
    studioVersion === StudioProjectVersion.Universal
      ? undefined
      : getProjectVersionIcon(studioVersion);
  const displayVersionDisplayName =
    versionDisplayName === 'universal' ? undefined : versionDisplayName;
  return (
    <>
      {clientName && <CardDescription text={clientName || ''} />}
      <Box
        display="grid"
        gridAutoFlow="column"
        justifyContent="start"
        style={{ gap: 8 }}
      >
        {icon && <Icon type={icon} style={{ color: palette.gray_75 }} />}
        {displayVersionDisplayName && (
          <Typography variant="pSm" color="secondary">
            {displayVersionDisplayName}
          </Typography>
        )}
      </Box>
    </>
  );
};

const ProjectCardFooter = ({
  clipsCount,
  projectOwner,
  isWellSaidCreator,
  studioVersion,
  hasTeam,
  creatorId,
}: Pick<Project, 'clipsCount' | 'studioVersion' | 'creatorId'> & {
  projectOwner?: Pick<TeamMember, 'id' | 'initials' | 'email'>;
  isWellSaidCreator: boolean;
  hasTeam: boolean;
}) => {
  const clipsCountText =
    studioVersion === StudioProjectVersion.ScriptStudio ||
    studioVersion === StudioProjectVersion.Universal
      ? 'section'
      : 'clip';

  return (
    <CardFooter
      icon={
        studioVersion === StudioProjectVersion.ScriptStudio ||
        studioVersion === StudioProjectVersion.Universal
          ? 'clapperBoard'
          : 'paperclip'
      }
      text={`${clipsCount} ${clipsCountText}${clipsCount === 1 ? '' : 's'}`}
      slots={{
        menu: (
          <OwnerBubble
            isWellSaidCreator={isWellSaidCreator}
            projectOwner={projectOwner}
            hasTeam={hasTeam}
            creatorId={creatorId}
          />
        ),
      }}
    />
  );
};

const useStyles = makeStyles()(() => ({
  card: {
    height: PROJECT_CARD_HEIGHT,
    minWidth: PROJECT_CARD_MIN_WIDTH,
  },
}));

const ProjectCardHeader = ({
  title,
  href,
  slots,
  id,
  checked,
  onSelect,
  disableSelect,
}: ProjectCardHeaderProps) => {
  const canSelectProject = useCanSelectProject();

  return (
    <CardHeader
      title={title}
      href={href}
      slots={{
        ...slots,
        icon: disableSelect ? (
          <Icon type={'fileProject'} />
        ) : (
          <IconCheckboxInput
            id={id}
            type={'fileProject'}
            checked={checked}
            onSelect={onSelect}
            disableCheckbox={!canSelectProject || disableSelect}
          />
        ),
      }}
    />
  );
};

export const BaseProjectCard = (props: BaseProjectCardProps) => {
  const {
    selected = false,
    project,
    href,
    onClick,
    onSelect,
    onKeyPress,
    slots,
    teamData,
    disableSelect,
  } = props;
  const { id } = project;

  const hasTeam = !!teamData?.user?.team;
  const teamMembers = teamData?.user?.team?.members;

  const canSelectProject = useCanSelectProject();

  const projectOwner = !canSelectProject
    ? undefined
    : teamMembers?.find(member => project.creatorId === member.id);
  const isWellSaidCreator = project.creatorId === 'wellsaid';

  const { classes } = useStyles();

  const handleSelect = () => {
    if (onSelect) onSelect(!selected);
  };

  return (
    <Card
      className={classes.card}
      selected={selected}
      onClick={onClick}
      onKeyPress={onKeyPress}
    >
      <Box display="grid" style={{ gap: 8 }} minWidth={'100%'}>
        <ProjectCardHeader
          id={id}
          title={project.name}
          href={href}
          checked={selected}
          onSelect={handleSelect}
          slots={slots}
          disableSelect={disableSelect}
        />
        <ProjectCardBody
          clientName={project.clientName}
          studioVersion={project.studioVersion}
        />
      </Box>
      <ProjectCardFooter
        clipsCount={project.clipsCount}
        projectOwner={projectOwner}
        isWellSaidCreator={isWellSaidCreator}
        studioVersion={project.studioVersion}
        hasTeam={hasTeam}
        creatorId={project.creatorId}
      />
    </Card>
  );
};

export const ProjectCard = (props: ProjectCardProps) => {
  const {
    project,
    currentDrive,
    currentFolder,
    disableSelect,
    pageOrigin,
    ...rest
  } = props;
  const { href } = props;
  const { data } = useUserContext();
  const { projectAbility } = useProjectAccessAbility({
    projectId: project.id,
    projectAccess: project.projectAccess,
  });
  const hasReadOnlyPermissions =
    currentDrive?.driveAccess?.role === DriveAccessRoleEnum.ReadOnly;

  if (!data) return null;
  if (!project.projectAccess?.viewerAccessRole) return null;

  return (
    <ProjectAccessProvider
      ability={projectAbility || JSON.stringify({ A: [] })}
    >
      <BaseProjectCard
        currentDrive={currentDrive}
        project={project}
        disableSelect={disableSelect}
        slots={{
          menu: (
            <ProjectActionMenu
              project={project}
              href={href}
              currentDriveId={currentDrive?.id}
              currentFolderId={currentFolder?.id}
              isSharedDrive={
                !currentDrive || currentDrive.name === DriveType.Shared
              }
              hasReadOnlyPermissions={hasReadOnlyPermissions}
              disableSelect={disableSelect}
              pageOrigin={pageOrigin}
            />
          ),
        }}
        teamData={data}
        {...rest}
      />
    </ProjectAccessProvider>
  );
};

export default ProjectCard;
